import Vue from 'vue'
import axios from 'axios'

import { DEV_TOOLS, API_URL } from '@/config.js'
import i18nWrapper from '@/plugins/i18n'
import fakeCredentials from '@/services/_devTools/fakeCredentials'

const okApi = axios.create({
  baseURL: API_URL,
  withCredentials: true,
})

if (DEV_TOOLS) {
  okApi.defaults.transformRequest.push((data, headers) => {
    return fakeCredentials.transformRequest(data, headers)
  })
}

okApi.interceptors.response.use(
  (response) => response,
  (error) => {
    error.isAxios = true
    throw error
  },
)

// helper functions
const returnData = (response) => response.data

const handleServerError = (err) => {
  console.warn('unexpected error', err)
  if (!err.response) {
    Vue.notify({
      title: 'Server error',
      text: i18nWrapper.getI18n().t('api.serverOrNetworkError'),
      type: 'danger',
    })
    throw null
  }

  throw err.response
}

// api resources
export const fetchState = (formId) => {
  return okApi.get(`question-form/${formId}/state`).then(returnData).catch(handleServerError)
}

export const fetchMetadata = () => {
  return okApi.get('code/metadata').then(returnData)
}

export const fetchEnum = (endpoint, params) => {
  return okApi.get(`code/${endpoint}`, { params }).then(returnData)
}

export const formPartAResource = {
  fetchData({ formId, accessToken }) {
    const request = accessToken
      ? okApi.get(`question-form/${formId}/a/${accessToken}`)
      : okApi.get(`question-form/${formId}/a`)

    return request
  },
  send({ formId, accessToken }, payload) {
    const request = accessToken
      ? okApi.post(`question-form/${formId}/a/${accessToken}`, payload)
      : okApi.post(`question-form/${formId}/a`, payload)

    return request.then(returnData).catch(handleServerError)
  },
  saveDraft({ formId, accessToken }, payload) {
    return okApi
      .post(`question-form/${formId}/a/${accessToken}/draft`, payload)
      .then(returnData)
      .catch(handleServerError)
  },
  editBeforeImport(formId, payload) {
    return okApi
      .post(`question-form/${formId}/a/before-import`, payload)
      .then(returnData)
      .catch(handleServerError)
  },
  // cancelForm(formId, accessToken) {
  //   return okApi
  //     .put(`question-form/a/${formId}/cancell`, { accessToken })
  //     .then(returnData)
  //     .catch(handleServerError);
  // },
}

export const formPartBResource = {
  fetchData({ formId }) {
    return okApi.get(`question-form/${formId}/b`)
  },
  async send(formId, payload) {
    try {
      const response = await okApi.post(`question-form/${formId}/b`, payload)
      return returnData(response)
    } catch (err) {
      try {
        handleServerError(err)
      } catch (response) {
        const { data } = response

        if (response.status === 502) {
          Vue.notify({
            title: 'Import do OK Base skončil chybou',
            text: data.detail,
            type: 'danger',
            duration: -1,
          })
          throw null
        }
        throw response
      }
    }
  },
  saveDraft(formId, payload) {
    return okApi
      .post(`question-form/${formId}/b/draft`, payload)
      .then(returnData)
      .catch(handleServerError)
  },
  editBeforeImport(formId, payload) {
    return okApi
      .post(`question-form/${formId}/b/before-import`, payload)
      .then(returnData)
      .catch(handleServerError)
  },
}

export const managementResource = {
  fetchAll() {
    return okApi.get('question-form').then(returnData).catch(handleServerError)
  },
  fetchDetail({ formId }) {
    return okApi.get(`question-form/${formId}/detail`)
  },
  fetchPossibleManagers() {
    return okApi.get('question-form/possible-managers').then(returnData).catch(handleServerError)
  },
  fetchPossibleManagersForCurrentUser() {
    return okApi
      .get('question-form/possible-managers-for-current-user')
      .then(returnData)
      .catch(handleServerError)
  },
  createEmployee(payload) {
    return okApi.post('question-form', payload).then(returnData).catch(handleServerError)
  },
  changeManager(formId, payload) {
    return okApi
      .put(`question-form/${formId}/change-manager`, payload)
      .then(returnData)
      .catch(handleServerError)
  },
  confirmImport({ formId }) {
    return okApi.post(`question-form/${formId}/ok`)
  },
  cancelForm({ formId }) {
    return okApi.put(`question-form/${formId}/cancell`)
  },
}

export const userResource = {
  fetchInfo() {
    return okApi.get('user').then(returnData).catch(handleServerError)
  },
}
